import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { connect } from 'react-redux';
import documentsBasket from '@/assets/images/documentsBasket.png';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import Info from '@/assets/icons/Info';
import ContentLoader from '@/components/loaders/ContentLoader';
import { useToast } from '@/hooks/use-toast';
import useDesktop from '../../../../hooks/useDesktop';
import PageLayout from '../../../../components/layouts/WithHeader';
import MDRForm from '../../../../components/Jobs/MDRForm';
import SentMDR from '../../../../components/Jobs/SentMDR';
import { JobStates } from '../../../../utils/jobsUtils';
import { useActiveProperty } from '../../../../store/hooks';
import {
  cancelMissingDocumentsRequest,
  fetchMDRList,
} from '../../../../api/jobs';
import styles from './styles';
import translations from './translations.json';

const CreateMDR = ({ classes, language = 'nb' }) => {
  // ------------------------------------------------------------------------
  // --------------------------STATE & HOOKS---------------------------------
  // ------------------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(true);
  const pageTranslations = translations[language];
  const { property } = useActiveProperty();
  const isDesktop = useDesktop();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ------------------------------------------------------------------------
  // ----------------------------HANDLERS------------------------------------
  // ------------------------------------------------------------------------
  const filterSentMDR = (item) =>
    item.boligmappaNumber === property?.boligmappaNumber &&
    item.status === JobStates.Pending;
  const { data: sentRequests } = useQuery({
    queryKey: [
      'sentMDRs',
      {
        boligmappaNumber: property?.boligmappaNumber,
      },
    ],
    enabled: !!property,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
    queryFn: async () => {
      setIsLoading(true);
      let data = [];
      await fetchMDRList(property.boligmappaNumber)
        .then((res) => {
          if (res[1]) {
            const newSentRequests = res[1].jobs.filter((item) =>
              filterSentMDR(item)
            );
            data = newSentRequests ?? [];
          }
        })
        .finally(() => {
          setIsLoading(false);
        });

      return data;
    },
  });

  const onRemoveMDR = async (requestToRemove) => {
    await cancelMissingDocumentsRequest(requestToRemove.jobNumber)
      .then((res) => {
        if (!res[1]?.success) {
          toast({
            title: `${pageTranslations.anErrorOccured} ${pageTranslations.pleaseTryAgain}`,
            variant: 'destructive',
          });
        } else {
          queryClient.setQueryData(
            ['sentMDRs', { boligmappaNumber: property?.boligmappaNumber }],
            (oldData) => {
              if (!oldData) return [];
              return oldData.filter(
                (item) => item.jobNumber !== requestToRemove.jobNumber
              );
            }
          );
          toast({ title: pageTranslations.requestRemoved, variant: 'success' });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const boldifyText = (text) => {
    const parts = text.split('\b');
    if (parts.length > 1) return <b>{parts[1]}</b>;

    return text;
  };

  const renderAccordionContent = (content) =>
    content.split('\n').map((text, i) => {
      const uniqueKey = `no-id-${i}`;
      return (
        <span key={uniqueKey}>
          {boldifyText(text)}
          <br />
        </span>
      );
    });

  useEffect(() => {
    if (sentRequests) {
      setIsLoading(false);
    }
  }, []);

  return (
    <PageLayout
      noPaddingTop
      minFullHeight
      classes={{ title: classes.pageTitle }}
      noPadding={!isDesktop}
      lessMarginLeft={isDesktop}
      headerTitle="Skaff dokumentasjon"
      maxWidth="xl"
    >
      <div className="sm:py-8 py-0 font-circular">
        <div className="flex text-foreground flex-col items-center pb-9">
          <img alt="Missing documents" width={39} src={documentsBasket} />
          <h2 className="font-bold sm:text-[42px] text-[28px]">
            Skaff dokumentasjon
          </h2>
          <p className="sm:text-sm text-[12px] text-muted-foreground">
            Vi kontakter håndverkeren på dine vegne.
          </p>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 w-full gap-5 text-foreground">
          <MDRForm labels={pageTranslations} />
          <div className="flex order-first xl:order-none flex-col bg-card rounded-[8px] pb-4 xl:pb-0">
            <h3 className="sm:p-4 p-2 pb-2 sm:text-[22px] text-[18px] font-bold">
              Aktive forespørsler
            </h3>
            {isLoading ? (
              <>
                <ContentLoader className="my-2" width="100%" height={50} />
                <ContentLoader className="my-2" width="100%" height={50} />
                <ContentLoader className="my-2" width="100%" height={50} />
              </>
            ) : (
              <>
                {sentRequests.length ? (
                  <SentMDR sentRequests={sentRequests} onRemove={onRemoveMDR} />
                ) : (
                  <div className="flex flex-col max-w-[345px] h-[80%] justify-center self-center items-center text-center">
                    <img
                      alt="Missing documents"
                      width={62}
                      src={documentsBasket}
                    />
                    <h3 className="pb-2 sm:text-[22px] text-[18px] font-bold tracking-tighter">
                      Ingen aktive forespørsler ennå
                    </h3>
                    <p className="sm:text-base text-[14px] leading-6 tracking-tighter">
                      Usikker på hva du kan be om?
                      <br /> Se eksempler i “Ofte stilte spørsmål” nedenfor.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex flex-col p-5 bg-card rounded-[8px]">
            <h3 className="sm:text-[22px] text-[18px] leading-[36px] pb-2 font-bold tracking-tighter">
              Derfor er dokumentasjon viktig:
            </h3>
            <div className="flex sm:p-4 p-2 border border-dashed border-rose-200 rounded-[6px]">
              <Info
                className="mt-[6px] mr-[6px] shrink-0"
                width="14"
                height="14"
                fill="#0DB71E"
              />
              <div className="flex flex-col gap-2">
                <h4 className="sm:text-[20px] text-[14px] font-bold">
                  En veldokumentert bolig gir deg best forutsetning ved et
                  fremtidig boligsalg eller refinansiering av lån.
                </h4>
                <p className="sm:text-[18px] text-[14px]">
                  Har du ikke dokumentasjon fra rørleggeren, elektrikeren eller
                  andre håndverkere kan oppussing og annet arbeid bli verdiløst.
                  Om du mangler dokumentasjon fra håndverkere du har brukt, er
                  det lurt å be om dette nå så du har det i orden.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col p-5 bg-card rounded-[8px]">
            <h3 className="sm:text-[22px] text-[18px] leading-[36px] font-bold tracking-tighter">
              Ofte stilte spørsmål
            </h3>
            <Accordion type="single" collapsible>
              <AccordionItem value="item-1">
                <AccordionTrigger className="sm:text-base text-[14px] font-bold">
                  {pageTranslations.faqQuestion1}
                </AccordionTrigger>
                <AccordionContent>
                  {renderAccordionContent(pageTranslations.faqQuestion1Content)}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="sm:text-base text-[14px] font-bold">
                  {pageTranslations.faqQuestion2}
                </AccordionTrigger>
                <AccordionContent>
                  {renderAccordionContent(pageTranslations.faqQuestion2Content)}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger className="sm:text-base text-[14px] font-bold">
                  {pageTranslations.faqQuestion3}
                </AccordionTrigger>
                <AccordionContent>
                  {renderAccordionContent(pageTranslations.faqQuestion3Content)}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4">
                <AccordionTrigger className="sm:text-base text-[14px] font-bold">
                  {pageTranslations.faqQuestion4}
                </AccordionTrigger>
                <AccordionContent>
                  {renderAccordionContent(pageTranslations.faqQuestion4Content)}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, null)(withStyles(styles)(CreateMDR));
